<!-- Body -->
<div class="page-container" [class.loading]="isLoading">
    <app-header title="{{ 'account.employeeProfile' | appTranslate }}" [menuService]="tabMenuService">
        <div class="buttons">
            <ng-container *ngIf="canCreateChangeRequest">
                <ui-button type="outline" [matMenu]="changeRequestMenu"
                    >{{ 'account.requestChange' | appTranslate }} <mat-icon svgIcon="chevronDown"></mat-icon>
                </ui-button>
                <mat-menu #changeRequestMenu="matMenu">
                    <button
                        *ngFor="let template of changeRequestTemplates"
                        mat-menu-item
                        [routerLink]="['/change-requests', 'templates', template.id, 'employees', employee.id, 'new']"
                    >
                        {{ changeRequestTemplate[template.name] | translate }}
                    </button>
                </mat-menu>
            </ng-container>
        </div>
    </app-header>

    <app-warning-banner [warningsToDisplayProvider]="warningProvider" [withMargin]="false"></app-warning-banner>

    <div class="profile-layout">
        <app-profile-card class="profile-layout-sidebar" [model]="employee"></app-profile-card>
        <div class="profile-layout-content">
            @if (isKYCVisibleForCurrentUser()) {
                <ui-card class="profile-card" role="group" aria-labelledby="kyc-kyb-header" [withMargin]="false">
                    <app-kyc-kyb-status scope="KYC" />
                </ui-card>
            }
            <!-- Basic Information -->
            <ui-card class="profile-card" role="group" aria-labelledby="basic-info-header" [withMargin]="false">
                <div class="profile-card-header">
                    <h2 id="basic-info-header">{{ 'account.basicInformation' | appTranslate }}</h2>
                    <ui-button type="link" (click)="openEditBasicInfoDialog()">{{ 'edit' | appTranslate }}</ui-button>
                </div>

                <label>{{ 'account.firstName' | appTranslate }}</label>
                <div>{{ employee.firstName }}</div>
                <label>{{ 'account.lastName' | appTranslate }}</label>
                <div>{{ employee.lastName }}</div>
                <label>{{ 'account.phoneWork' | appTranslate }}</label>
                <div>{{ employee.phoneWork }}</div>
                <ng-container *ngIf="employee?.genders?.length">
                    <label>{{ 'account.gender' | appTranslate }}</label>
                    <div>
                        <ng-container
                            *ngFor="let genderToDisplay of employee?.genders ?? [] | genders; let last = last"
                        >
                            {{ genderToDisplay | appTranslate }}<span *ngIf="!last">,</span>
                        </ng-container>
                    </div>
                </ng-container>
                <ng-container *ngIf="employee?.pronouns?.length">
                    <label>{{ 'account.pronouns' | appTranslate }}</label>
                    <div>
                        <ng-container
                            *ngFor="let pronounToDisplay of employee?.pronouns ?? [] | pronouns; let last = last"
                        >
                            {{ pronounToDisplay | appTranslate }}<span *ngIf="!last">,</span>
                        </ng-container>
                    </div>
                </ng-container>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.basic_information, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <!-- Personal Information -->
            <ui-card class="profile-card" role="group" aria-labelledby="personal-info-header" [withMargin]="false">
                <div class="profile-card-header">
                    <h2 id="personal-info-header">{{ 'account.personalInformation' | appTranslate }}</h2>
                    <ui-button type="link" (click)="currentlyOpenDialog = 'PERSONAL_INFO'">{{
                        'edit' | appTranslate
                    }}</ui-button>
                </div>
                <!-- Show/Hide Legal Name based on Legal name permission -->
                <ng-container *permission="viewLegalNamePermission">
                    <label>{{ 'account.legalFirstName' | appTranslate }}</label>
                    <div>{{ employee.account.legalFirstName }}</div>
                    <label>{{ 'account.legalMiddleName' | appTranslate }}</label>

                    <div>{{ employee.account.legalMiddleName }}</div>
                    <label>{{ 'account.legalLastName' | appTranslate }}</label>
                    <div>{{ employee.account.legalLastName }}</div>
                </ng-container>

                <!-- Show/Hide Legal Sex based on Legal sex permission -->
                <ng-container *permission="viewLegalSexPermission">
                    <label>{{ 'account.legalSex' | appTranslate }}</label>
                    <div>{{ employee.legalSex | legalSex | appTranslate }}</div>
                </ng-container>

                <label>{{ 'account.dateOfBirth' | translate }}</label>
                <div *ngIf="employee.bornOn">
                    {{ employee.bornOn | appDate: 'mediumDate' }}
                    {{ 'account.age' | translate: { age: employee.age } }}
                </div>
                <ng-container *ngIf="shouldShowBirthdayVisibility">
                    <label>{{ 'account.birthdayVisibility' | translate }}</label>
                    <div *ngIf="employee.isBirthdayPrivate; else birthdayPublic">
                        {{ 'account.birthdayPrivateOption' | translate }}
                    </div>
                    <ng-template #birthdayPublic>{{ 'account.birthdayPublicOption' | translate }}</ng-template>
                </ng-container>

                <label>{{ 'account.sin' | appTranslate }}</label>
                <div class="blur">{{ employee.sin }}</div>

                <label>{{ 'account.phoneMobile' | appTranslate }}</label>
                <div>{{ employee.phoneMobile }}</div>
                <label>{{ 'account.phoneHome' | appTranslate }}</label>
                <div>{{ employee.phoneHome }}</div>
                <label>{{ 'account.dietaryRestrictions' | appTranslate }}</label>
                <div>{{ employee.dietaryRestrictions }}</div>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.personal_information, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <!-- Address -->

            <ui-card class="profile-card" role="group" aria-labelledby="address-header" [withMargin]="false">
                <div class="profile-card-header">
                    <h2 id="address-header">{{ 'account.address' | appTranslate }}</h2>
                    <ui-button type="link" (click)="currentlyOpenDialog = 'ADDRESS'">{{
                        'edit' | appTranslate
                    }}</ui-button>
                </div>
                <label>{{ 'account.streetAddress' | translate }}</label>
                <div>{{ address.addressLine1 }}</div>
                <label>{{ 'account.unitSuite' | translate }}</label>
                <div>{{ address.addressLine2 }}</div>
                <label>{{ 'account.city' | translate }}</label>
                <div>{{ address.city }}</div>
                <label>{{ 'account.zipPostalCode' | translate }}</label>
                <div>{{ address.postalCode }}</div>
                <label>{{ 'account.provinceState' | translate }}</label>
                <div>{{ address.provinceDisplayName }}</div>
                <label>{{ 'account.country' | translate }}</label>
                <div>{{ address.country | i18nCountry }}</div>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.address, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <!-- Emergency Contacts -->

            <ui-card class="profile-card" role="group" aria-labelledby="emergency-header" [withMargin]="false">
                <div class="profile-card-header">
                    <h2 id="emergency-header">{{ 'account.emergencyContacts' | appTranslate }}</h2>
                    <ui-button type="link" (click)="currentlyOpenDialog = 'EMERGENCY_CONTACTS'">{{
                        'edit' | appTranslate
                    }}</ui-button>
                </div>
                <label>{{ 'account.primaryName' | translate }}</label>
                <div>{{ employee.ecPrimaryName }}</div>
                <label>{{ 'account.primaryRelation' | translate }}</label>
                <div>{{ employee.ecPrimaryRelation }}</div>
                <label>{{ 'account.primaryPhone' | translate }}</label>
                <div>{{ employee.ecPrimaryPhone }}</div>
                <label>{{ 'account.secondaryName' | translate }}</label>
                <div>{{ employee.ecSecondaryName }}</div>
                <label>{{ 'account.secondaryRelation' | translate }}</label>
                <div>{{ employee.ecSecondaryRelation }}</div>
                <label>{{ 'account.secondaryPhone' | translate }}</label>
                <div>{{ employee.ecSecondaryPhone }}</div>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.emergency_contacts, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <app-data-field-groups
                *ngIf="dataFields && dataValues"
                [withMargin]="false"
                [dataFields]="dataFields"
                [dataValues]="dataValues"
                [canEditDataValues]="canEditDataValues"
                component="personal"
                (edit)="onDataFieldEdit($event)"
            ></app-data-field-groups>
        </div>
    </div>
</div>

<!-- Modals -->
<ui-dialog
    [open]="currentlyOpenDialog === 'BASIC_INFO'"
    [form]="editFormBasicInformationForm.form"
    (primaryButtonPressed)="filterNullValuesFromGendersAndPronouns(); submitForm(editFormBasicInformationForm)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    header="account.editBasicInformation"
>
    <app-form-basic-information
        #editFormBasicInformationForm
        [employee]="employee"
        description="{{ 'account.identifyYourselfToYourColleagues' | translate }}"
    ></app-form-basic-information>

    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'BASIC_INFO'"
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.basic_information"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'PERSONAL_INFO'"
    [form]="editFormPersonalInformationForm.form"
    (primaryButtonPressed)="submitForm(editFormPersonalInformationForm)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    header="account.editPersonalInformation"
>
    <app-form-account-personal-information
        #editFormPersonalInformationForm
        [account]="account"
        [employee]="employee"
    ></app-form-account-personal-information>

    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'PERSONAL_INFO'"
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.personal_information"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'ADDRESS'"
    [form]="addressForm.form"
    (primaryButtonPressed)="submitForm(addressForm)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    header="account.editAddress"
>
    <app-form-address #addressForm [address]="address"></app-form-address>
    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'ADDRESS'"
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.address"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'EMERGENCY_CONTACTS'"
    [form]="editFormEc.form"
    (primaryButtonPressed)="submitForm(editFormEc)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    header="account.editEmergencyContacts"
>
    <app-form-emergency-contacts #editFormEc [employee]="employee"></app-form-emergency-contacts>
    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'EMERGENCY_CONTACTS'"
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.emergency_contacts"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'CUSTOM_DATA_GROUP'"
    [form]="editDataFieldsForm.form"
    (primaryButtonPressed)="submitForm(editDataFieldsForm, true)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    [headerButton]="
        showAddCustomFieldsButton
            ? { label: 'account.addCustomFields', onPress: onAddCustomFields.bind(this) }
            : undefined
    "
    header="edit"
>
    <app-form-data-fields
        #editDataFieldsForm
        [employee]="employee"
        [dataFieldGroup]="editFieldGroup"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
    ></app-form-data-fields>
</ui-dialog>
